import React, { useState, useEffect } from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { Save, Building, Clock, Settings as SettingsIcon, AlertCircle } from 'lucide-react';
import { Restaurant } from '../../types';
import NotificationSettings from './NotificationSettings';

const RestaurantSettings: React.FC = () => {
  const { restaurant, updateRestaurant, isLoading } = useRestaurant();
  const [formData, setFormData] = useState<Partial<Restaurant>>({});
  
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Update form data when restaurant data is loaded
  useEffect(() => {
    if (restaurant) {
      setFormData({
        nome: restaurant.nome || '',
        contacto: {
          telefone: restaurant.contacto?.telefone || '',
          email: restaurant.contacto?.email || '',
        },
        localizacao: {
          morada: restaurant.localizacao?.morada || '',
          codigo_postal: restaurant.localizacao?.codigo_postal || '',
          cidade: restaurant.localizacao?.cidade || '',
        },
        horarios: restaurant.horarios || {
          segunda: { abertura: '09:00', fecho: '22:00', ativo: true },
          terca: { abertura: '09:00', fecho: '22:00', ativo: true },
          quarta: { abertura: '09:00', fecho: '22:00', ativo: true },
          quinta: { abertura: '09:00', fecho: '22:00', ativo: true },
          sexta: { abertura: '09:00', fecho: '22:00', ativo: true },
          sabado: { abertura: '09:00', fecho: '22:00', ativo: true },
          domingo: { abertura: '09:00', fecho: '22:00', ativo: false },
        },
        horas_de_siesta: restaurant.horas_de_siesta || {
          ativo: false,
          comeco: '15:00',
          reabertura: '18:00',
        },
        config_reservas: restaurant.config_reservas || {
          max_por_horario: 10,
          antecedencia_minima_horas: 2,
          antecedencia_maxima_dias: 30,
          requer_confirmacao: true,
          duracao_media_minutos: 120,
          intervalo_slots_minutos: 30,
        },
        config_pagamento: restaurant.config_pagamento || {
          requer_taxa: false,
          valor_taxa: 0,
          tipo_taxa: 'fixo',
          descricao_taxa: 'Taxa de reserva',
        },
      });
    }
  }, [restaurant]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await updateRestaurant(formData);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar configurações');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (section: keyof Partial<Restaurant>, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...(prev[section] as any),
        [field]: value,
      },
    }));
  };

  const handleHorarioChange = (dia: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      horarios: {
        ...prev.horarios,
        [dia]: {
          abertura: prev.horarios?.[dia]?.abertura || '09:00',
          fecho: prev.horarios?.[dia]?.fecho || '22:00',
          ativo: prev.horarios?.[dia]?.ativo || false,
          ...prev.horarios?.[dia],
          [field]: value,
        },
      },
    }));
  };

  const handleConfigChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      config_reservas: {
        max_por_horario: prev.config_reservas?.max_por_horario || 10,
        antecedencia_minima_horas: prev.config_reservas?.antecedencia_minima_horas || 2,
        antecedencia_maxima_dias: prev.config_reservas?.antecedencia_maxima_dias || 30,
        requer_confirmacao: prev.config_reservas?.requer_confirmacao || true,
        duracao_media_minutos: prev.config_reservas?.duracao_media_minutos || 120,
        intervalo_slots_minutos: prev.config_reservas?.intervalo_slots_minutos || 30,
        ...prev.config_reservas,
        [field]: value,
      },
    }));
  };

  const handleSiestaChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      horas_de_siesta: {
        ativo: prev.horas_de_siesta?.ativo || false,
        comeco: prev.horas_de_siesta?.comeco || '15:00',
        reabertura: prev.horas_de_siesta?.reabertura || '18:00',
        ...prev.horas_de_siesta,
        [field]: value,
      },
    }));
  };

  const handlePaymentConfigChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      config_pagamento: {
        requer_taxa: prev.config_pagamento?.requer_taxa || false,
        valor_taxa: prev.config_pagamento?.valor_taxa || 0,
        tipo_taxa: prev.config_pagamento?.tipo_taxa || 'fixo',
        descricao_taxa: prev.config_pagamento?.descricao_taxa || 'Taxa de reserva',
        ...prev.config_pagamento,
        [field]: value,
      },
    }));
  };

  const diasSemana = [
    { key: 'segunda', label: 'Segunda-feira' },
    { key: 'terca', label: 'Terça-feira' },
    { key: 'quarta', label: 'Quarta-feira' },
    { key: 'quinta', label: 'Quinta-feira' },
    { key: 'sexta', label: 'Sexta-feira' },
    { key: 'sabado', label: 'Sábado' },
    { key: 'domingo', label: 'Domingo' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <SettingsIcon className="h-8 w-8 animate-spin text-primary-600 mx-auto" />
          <p className="mt-2 text-sm text-secondary-600">A carregar configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold leading-7 text-secondary-900 sm:truncate sm:text-3xl">
            Configurações do Restaurante
          </h1>
          <p className="mt-1 text-sm text-secondary-500">
            Gerir informações e configurações do restaurante
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center space-x-2">
            <Save className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-700">Configurações atualizadas com sucesso!</span>
          </div>
        )}

        {/* Basic Information */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-secondary-900 mb-4 flex items-center space-x-2">
            <Building className="h-5 w-5" />
            <span>Informações Básicas</span>
          </h2>

          <div className="grid grid-cols-1 gap-6">
            <div>
              <label htmlFor="nome" className="label">
                Nome do Restaurante
              </label>
              <input
                type="text"
                id="nome"
                className="input-field"
                value={formData.nome}
                onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="telefone" className="label">
                  Telefone
                </label>
                <input
                  type="tel"
                  id="telefone"
                  className="input-field"
                  value={formData.contacto?.telefone}
                  onChange={(e) => handleChange('contacto', 'telefone', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="email" className="label">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  className="input-field"
                  value={formData.contacto?.email}
                  onChange={(e) => handleChange('contacto', 'email', e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="morada" className="label">
                Morada
              </label>
              <input
                type="text"
                id="morada"
                className="input-field"
                value={formData.localizacao?.morada}
                onChange={(e) => handleChange('localizacao', 'morada', e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="codigo_postal" className="label">
                  Código Postal
                </label>
                <input
                  type="text"
                  id="codigo_postal"
                  className="input-field"
                  value={formData.localizacao?.codigo_postal}
                  onChange={(e) => handleChange('localizacao', 'codigo_postal', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="cidade" className="label">
                  Cidade
                </label>
                <input
                  type="text"
                  id="cidade"
                  className="input-field"
                  value={formData.localizacao?.cidade}
                  onChange={(e) => handleChange('localizacao', 'cidade', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Operating Hours */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-secondary-900 mb-4 flex items-center space-x-2">
            <Clock className="h-5 w-5" />
            <span>Horários de Funcionamento</span>
          </h2>

          <div className="space-y-4">
            {diasSemana.map(({ key, label }) => (
              <div key={key} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`${key}_ativo`}
                    checked={formData.horarios?.[key]?.ativo || false}
                    onChange={(e) => handleHorarioChange(key, 'ativo', e.target.checked)}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor={`${key}_ativo`} className="text-sm font-medium text-secondary-700">
                    {label}
                  </label>
                </div>
                <div>
                  <label htmlFor={`${key}_abertura`} className="label text-xs">
                    Abertura
                  </label>
                  <input
                    type="time"
                    id={`${key}_abertura`}
                    className="input-field"
                    value={formData.horarios?.[key]?.abertura || ''}
                    onChange={(e) => handleHorarioChange(key, 'abertura', e.target.value)}
                    disabled={!formData.horarios?.[key]?.ativo}
                  />
                </div>
                <div>
                  <label htmlFor={`${key}_fecho`} className="label text-xs">
                    Fecho
                  </label>
                  <input
                    type="time"
                    id={`${key}_fecho`}
                    className="input-field"
                    value={formData.horarios?.[key]?.fecho || ''}
                    onChange={(e) => handleHorarioChange(key, 'fecho', e.target.value)}
                    disabled={!formData.horarios?.[key]?.ativo}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Siesta Hours */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-secondary-900 mb-4 flex items-center space-x-2">
            <Clock className="h-5 w-5" />
            <span>Horas de Siesta</span>
          </h2>
          <p className="text-sm text-secondary-600 mb-4">
            Configure as horas em que o restaurante fecha entre o almoço e o jantar
          </p>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="siesta_ativo"
                checked={formData.horas_de_siesta?.ativo || false}
                onChange={(e) => handleSiestaChange('ativo', e.target.checked)}
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="siesta_ativo" className="text-sm font-medium text-secondary-700">
                Ativar horas de siesta
              </label>
            </div>

            {formData.horas_de_siesta?.ativo && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
                <div>
                  <label htmlFor="siesta_comeco" className="label">
                    Início da siesta
                  </label>
                  <input
                    type="time"
                    id="siesta_comeco"
                    className="input-field"
                    value={formData.horas_de_siesta?.comeco || ''}
                    onChange={(e) => handleSiestaChange('comeco', e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="siesta_reabertura" className="label">
                    Reabertura
                  </label>
                  <input
                    type="time"
                    id="siesta_reabertura"
                    className="input-field"
                    value={formData.horas_de_siesta?.reabertura || ''}
                    onChange={(e) => handleSiestaChange('reabertura', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Reservation Settings */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-secondary-900 mb-4 flex items-center space-x-2">
            <SettingsIcon className="h-5 w-5" />
            <span>Configurações de Reservas</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="max_por_horario" className="label">
                Máximo de reservas por horário
              </label>
              <input
                type="number"
                id="max_por_horario"
                min="1"
                max="100"
                className="input-field"
                value={formData.config_reservas?.max_por_horario}
                onChange={(e) => handleConfigChange('max_por_horario', parseInt(e.target.value))}
              />
            </div>

            <div>
              <label htmlFor="antecedencia_minima_horas" className="label">
                Antecedência mínima (horas)
              </label>
              <input
                type="number"
                id="antecedencia_minima_horas"
                min="0"
                max="168"
                className="input-field"
                value={formData.config_reservas?.antecedencia_minima_horas}
                onChange={(e) => handleConfigChange('antecedencia_minima_horas', parseInt(e.target.value))}
              />
            </div>

            <div>
              <label htmlFor="antecedencia_maxima_dias" className="label">
                Antecedência máxima (dias)
              </label>
              <input
                type="number"
                id="antecedencia_maxima_dias"
                min="1"
                max="365"
                className="input-field"
                value={formData.config_reservas?.antecedencia_maxima_dias}
                onChange={(e) => handleConfigChange('antecedencia_maxima_dias', parseInt(e.target.value))}
              />
            </div>

            <div>
              <label htmlFor="duracao_media_minutos" className="label">
                Duração média (minutos)
              </label>
              <input
                type="number"
                id="duracao_media_minutos"
                min="30"
                max="480"
                step="15"
                className="input-field"
                value={formData.config_reservas?.duracao_media_minutos}
                onChange={(e) => handleConfigChange('duracao_media_minutos', parseInt(e.target.value))}
              />
            </div>

            <div>
              <label htmlFor="intervalo_slots_minutos" className="label">
                Intervalo entre slots (minutos)
              </label>
              <input
                type="number"
                id="intervalo_slots_minutos"
                min="15"
                max="120"
                step="15"
                className="input-field"
                value={formData.config_reservas?.intervalo_slots_minutos}
                onChange={(e) => handleConfigChange('intervalo_slots_minutos', parseInt(e.target.value))}
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="requer_confirmacao"
                checked={formData.config_reservas?.requer_confirmacao || false}
                onChange={(e) => handleConfigChange('requer_confirmacao', e.target.checked)}
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="requer_confirmacao" className="text-sm font-medium text-secondary-700">
                Requerer confirmação manual das reservas
              </label>
            </div>
          </div>
        </div>

        {/* Payment Configuration */}
        <div className="card p-6">
          <h2 className="text-lg font-medium text-secondary-900 mb-4 flex items-center space-x-2">
            <SettingsIcon className="h-5 w-5" />
            <span>Configurações de Pagamento</span>
          </h2>
          <p className="text-sm text-secondary-600 mb-4">
            Configure taxas de reserva para cobrar dos clientes
          </p>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="requer_taxa"
                checked={formData.config_pagamento?.requer_taxa || false}
                onChange={(e) => handlePaymentConfigChange('requer_taxa', e.target.checked)}
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="requer_taxa" className="text-sm font-medium text-secondary-700">
                Requerer taxa de reserva
              </label>
            </div>

            {formData.config_pagamento?.requer_taxa && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
                <div>
                  <label htmlFor="valor_taxa" className="label">
                    Valor da taxa (€)
                  </label>
                  <input
                    type="number"
                    id="valor_taxa"
                    min="0"
                    step="0.01"
                    className="input-field"
                    value={formData.config_pagamento?.valor_taxa || 0}
                    onChange={(e) => handlePaymentConfigChange('valor_taxa', parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <label htmlFor="tipo_taxa" className="label">
                    Tipo de taxa
                  </label>
                  <select
                    id="tipo_taxa"
                    className="input-field"
                    value={formData.config_pagamento?.tipo_taxa || 'fixo'}
                    onChange={(e) => handlePaymentConfigChange('tipo_taxa', e.target.value)}
                  >
                    <option value="fixo">Valor fixo</option>
                    <option value="porcentagem">Porcentagem por pessoa</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="descricao_taxa" className="label">
                    Descrição da taxa
                  </label>
                  <input
                    type="text"
                    id="descricao_taxa"
                    className="input-field"
                    placeholder="Taxa de reserva"
                    value={formData.config_pagamento?.descricao_taxa || ''}
                    onChange={(e) => handlePaymentConfigChange('descricao_taxa', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="btn-primary flex items-center space-x-2"
          >
            {isSaving ? (
              <>
                <div className="spinner" />
                <span>A guardar...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Guardar Configurações</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Notification Settings - Separate from form */}
      <NotificationSettings />
    </div>
  );
};

export default RestaurantSettings;
